import React from 'react';
import type { QueryImageData } from './types';

export interface ImageLightboxProps {
  selectedImage: { url: string; presignedUrl: string } | null;
  onClose: () => void;
  queryImageData: QueryImageData | null;
  queryImageDataLoading: boolean;
  classificationInput: string;
  setClassificationInput: (v: string) => void;
  currentClassificationNames: string[];
  onAddClassification: () => void;
  onRemoveClassification: (name: string) => void;
  savingClassifications: boolean;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  selectedImage,
  onClose,
  queryImageData,
  queryImageDataLoading,
  classificationInput,
  setClassificationInput,
  currentClassificationNames,
  onAddClassification,
  onRemoveClassification,
  savingClassifications,
}) => {
  if (!selectedImage) return null;

  const classificationLabels = queryImageData?.ground_truth_labels?.filter(
    (l) => l.type === 'classification' || !l.bbox
  ) ?? [];
  const primary = classificationLabels[0];

  return (
    <div className="image-lightbox" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose}>×</button>
        {queryImageDataLoading && (
          <div style={{ padding: 16, color: '#666' }}>Loading labels...</div>
        )}
        {primary && (
          <div
            style={{
              marginBottom: 16,
              padding: 16,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 18,
            }}
          >
            <span style={{ opacity: 0.9 }}>Classification:</span>{' '}
            <strong>{primary.class_name}</strong>
            {(primary.confidence != null || primary.value != null) && (
              <span style={{ opacity: 0.95 }}>
                {' '}({((primary.confidence ?? primary.value ?? 1) * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        )}
        <img src={selectedImage.presignedUrl} alt={selectedImage.url} />
        {queryImageData?.ground_truth_labels && queryImageData.ground_truth_labels.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
            <h5 style={{ margin: '0 0 8px 0' }}>Labels ({queryImageData.ground_truth_labels.length})</h5>
            {queryImageData.ground_truth_labels.map((l, idx) => (
              <div key={idx} style={{ marginBottom: 6 }}>
                <strong>{l.class_name}</strong>
                {(l.confidence != null || l.value != null) && ` (${((l.confidence ?? l.value ?? 1) * 100).toFixed(1)}%)`}
                {l.bbox && ` · BBox: (${l.bbox.x1}, ${l.bbox.y1})–(${l.bbox.x2}, ${l.bbox.y2})`}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, padding: 12, background: '#f0f4ff', borderRadius: 8 }}>
          <h5 style={{ margin: '0 0 8px 0' }}>Classification labels</h5>
          <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#555' }}>
            Add or remove classification labels for this image. Image must exist in the database (from ingestion or ETL).
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              value={classificationInput}
              onChange={(e) => setClassificationInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onAddClassification()}
              placeholder="e.g. upright, vertical"
              style={{ flex: 1, padding: '8px 12px', borderRadius: 4, border: '1px solid #ccc' }}
            />
            <button
              type="button"
              onClick={onAddClassification}
              disabled={savingClassifications || !classificationInput.trim()}
              style={{
                padding: '8px 16px',
                background: classificationInput.trim() && !savingClassifications ? '#667eea' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: classificationInput.trim() && !savingClassifications ? 'pointer' : 'not-allowed',
              }}
            >
              {savingClassifications ? 'Saving...' : 'Add'}
            </button>
          </div>
          {currentClassificationNames.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {currentClassificationNames.map((name) => (
                <span
                  key={name}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginRight: 8,
                    marginBottom: 6,
                    padding: '4px 8px',
                    background: '#e0e7ff',
                    borderRadius: 4,
                    fontSize: 14,
                  }}
                >
                  {name}
                  <button
                    type="button"
                    onClick={() => onRemoveClassification(name)}
                    disabled={savingClassifications}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: savingClassifications ? 'not-allowed' : 'pointer',
                      padding: 0,
                      lineHeight: 1,
                      color: '#666',
                      fontSize: 16,
                    }}
                    aria-label={`Remove ${name}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
